import React, { ReactNode, useState, useEffect } from 'react';
import { VStack, Box, Checkbox } from 'native-base';
import { Plan } from '@springbig/shared';
import { Caption, H2, H3, H4 } from '~/shared/Typography';
import { useGeneralStyles } from '~/shared/styles';
import OurPressable from '~/shared/Pressable';

export type BillingSchedule = 'monthly' | 'yearly';

type SubscribeFormProps = {
    children: ReactNode;
    plan: Plan;
    onUpdateBillingSchedule: (value: BillingSchedule) => void;
    isLoading: boolean;
};

const SubscribeForm = ({
    children,
    plan,
    onUpdateBillingSchedule,
    isLoading
}: SubscribeFormProps) => {

    const gStyle = useGeneralStyles();
    const [isMonthly, setIsMonthly] = useState<boolean>(true);
    const [isYearly, setIsYearly] = useState<boolean>(false);

    useEffect(() => {
        onUpdateBillingSchedule(isMonthly ? 'monthly' : 'yearly');
    }, [isMonthly, isYearly, onUpdateBillingSchedule]);

    useEffect(() => {
        if (plan.billing_schedule === 'yearly') {
            setIsMonthly(false);
            setIsYearly(true);
        }
    }, [plan]);

    return (
        <VStack paddingTop={5} paddingBottom={8} space={8} px={4}>
            { plan.billing_schedule === 'both' &&
                <VStack space={2}>
                    <H2 textAlign={'center'}>Choose your plan</H2>

                    <OurPressable onPress={() => {
                        setIsMonthly(true); setIsYearly(false);
                    }}>
                        <Box
                            backgroundColor={gStyle.section_background}
                            padding={2}
                            flexDirection={'row'}
                            justifyContent={'space-between'}
                            alignItems={'flex-start'}
                            borderRadius={5}
                            borderWidth={2}
                            borderColor={isMonthly ? gStyle.button_background : 'transparent'}
                        >
                            <VStack space={2}>
                                <Box>
                                    <H3>Monthly</H3>
                                    <H4 fontSize={20}>${ plan.recurring_amount }/month</H4>
                                </Box>
                                { plan.trial_period > 0 &&
                                    <Box
                                        backgroundColor={'hsl(120, 40%, 80%)'}
                                        paddingX={1}
                                        borderRadius={3} w={'fit-content'}
                                    >
                                        <Caption
                                            fontWeight={700}
                                            color={'hsl(120, 40%, 40%)'}
                                            fontSize={12}
                                        >
                                            { plan.trial_period } day free trial
                                        </Caption>
                                    </Box>
                                }
                                <Caption opacity={.5}>
                                    Pay ${ plan.recurring_amount } per month. Cancel anytime.
                                </Caption>
                            </VStack>
                            <Checkbox
                                flex={1}
                                alignSelf={'flex-start'}
                                isChecked={isMonthly}
                                value={isMonthly.toString()}
                                isDisabled={isLoading}
                                onChange={(value) => {
                                    if (value) {
                                        setIsMonthly(value);
                                        setIsYearly(!value);
                                    }
                                }}
                                _checked={{
                                    value: '',
                                    bg: gStyle.button_background,
                                    _hover: {
                                        bg: gStyle.button_background,
                                    }
                                }}
                            />
                        </Box>
                    </OurPressable>

                    <OurPressable onPress={() => {
                        setIsMonthly(false); setIsYearly(true);
                    }}>
                        <Box
                            backgroundColor={gStyle.section_background}
                            padding={2}
                            flexDirection={'row'}
                            justifyContent={'space-between'}
                            alignItems={'flex-start'}
                            borderRadius={5}
                            borderWidth={2}
                            borderColor={isYearly ? gStyle.button_background : 'transparent'}
                        >
                            <VStack space={2}>
                                <Box>
                                    <H3>Yearly</H3>
                                    <H4 fontSize={20}>${ plan.yearly_recurring_amount }/year</H4>
                                </Box>
                                { plan.trial_period > 0 &&
                                    <Box
                                        backgroundColor={'hsl(120, 40%, 80%)'}
                                        paddingX={1}
                                        borderRadius={3} w={'fit-content'}
                                    >
                                        <Caption
                                            fontWeight={700}
                                            color={'hsl(120, 40%, 40%)'}
                                            fontSize={12}
                                        >{ plan.trial_period } day free trial</Caption>
                                    </Box>
                                }
                                <Caption opacity={.5}>
                                    Pay ${ plan.yearly_recurring_amount } per year. Cancel anytime.
                                </Caption>
                            </VStack>
                            <Checkbox
                                flex={1}
                                isChecked={isYearly}
                                value={isYearly.toString()}
                                isDisabled={isLoading}
                                onChange={(value) => {
                                    if (value) {
                                        setIsMonthly(!value);
                                        setIsYearly(value);
                                    }
                                }}
                                _checked={{
                                    value: '',
                                    bg: gStyle.button_background,
                                    _hover: {
                                        bg: gStyle.button_background,
                                    }
                                }}
                            />
                        </Box>
                    </OurPressable>
                </VStack>
            }

            <Box>
                <H2 textAlign={'center'}>Add card information</H2>

                { children }
            </Box>
        </VStack>
    );
};

export default SubscribeForm;
